import { AHTextFonts, AHThemeType, useTheme } from "arkh";

import Color from "color";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { getWidgetNoteTextShadowStyle } from "./getWidgetNoteTextShadowStyle";
import { widgetNoteFontSize } from "./widgetNoteFontSize";
import {
  useNoteWidgetStyleText_widgetNotePage$key,
  WidgetNoteFontSize,
  WidgetNoteFontStyle,
} from "./__generated__/useNoteWidgetStyleText_widgetNotePage.graphql";

export function getNoteWidgetStyle(
  backgroundColor: string | null,
  fontSize: WidgetNoteFontSize,
  fontStyle: WidgetNoteFontStyle,
  hasImageBackground: boolean,
  theme: AHThemeType
) {
  const fontSizeNumber = widgetNoteFontSize(fontSize);
  const fontColor = hasImageBackground
    ? theme.lightText
    : Color(backgroundColor ?? theme.surface).isDark()
    ? theme.lightText
    : theme.darkerText;
  const fontFamily = (() => {
    switch (fontStyle) {
      case "BOLD":
        return AHTextFonts.BOLDER;
      case "CURSIVE":
        return AHTextFonts.ITALIC;
      case "DEFAULT":
        return AHTextFonts.REGULAR;
      default:
        return AHTextFonts.REGULAR;
    }
  })();

  const fs = fontStyle === "CURSIVE" ? "italic" : "normal";
  return {
    color: fontColor,
    fontFamily,
    fontSize: fontSizeNumber,
    backgroundColor: backgroundColor ?? theme.surface,
    textAlign: fontSize === "LARGE" ? "center" : "left",
    fontStyle: fs,
    textShadow: hasImageBackground ? getWidgetNoteTextShadowStyle() : undefined,
  } as const;
}

export const WidgetNoteStyleLegcyToNewBG: { [key: string]: string } = {
  "rgb(0, 102, 255)": "#C9E5FF",
  "rgb(81, 224, 102)": "#C9FFD8",
  "rgb(255, 67, 90)": "#FFC9C9",
  "rgb(255, 220, 105)": "#FFF0C9",
  "#F5A623": "#FFF0C9",
};

export function useNoteWidgetPageTextStyle(
  pageKey: useNoteWidgetStyleText_widgetNotePage$key,
  options = {
    forceImage: false,
  }
) {
  const { backgroundColor, fontSize, fontStyle, image } = useFragment(
    graphql`
      fragment useNoteWidgetStyleText_widgetNotePage on WidgetNotePage {
        backgroundColor
        fontSize
        fontStyle
        image {
          id
        }
      }
    `,
    pageKey
  );

  const theme = useTheme();
  const resolvedBg = backgroundColor
    ? WidgetNoteStyleLegcyToNewBG[backgroundColor] ?? backgroundColor
    : theme.surface;

  return useMemo(
    () =>
      getNoteWidgetStyle(
        resolvedBg,
        fontSize,
        fontStyle,
        options.forceImage || !!image,
        theme
      ),
    [fontSize, fontStyle, image, options.forceImage, resolvedBg, theme]
  );
}
